import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { Button, Input, Select, Form, message } from "antd";
import GenericStatCard from "../../../../Components/Cards/GenericStatCard";
import creditIcon from "../../../../Assets/Icons/garage_money.svg";
import paymentIcon from "../../../../Assets/Icons/payments.svg";
import dataUsageIcon from "../../../../Assets/Icons/data_usage.svg";
import laptopCarIcon from "../../../../Assets/Icons/laptop_car.svg";
import {
  getCompanyCreditDetails,
  getCompanyModulesLookup,
  submitCreditAdjustment,
} from "../../../../API/Admin/CreditsBillings";

import "./style.css";
import usePageAccess from "../../../../Utlis/usePageAccess";

const { TextArea } = Input;

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar)) return avatar;
  let path = avatar.startsWith("/") ? avatar : `/${avatar}`;
  if (!path.startsWith("/media/")) path = `/media${path}`;
  return `${BACKEND_ORIGIN}${path}`;
};

const formatCurrency = (value) => `$${(parseFloat(value) || 0).toFixed(2)}`;

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (typeof data.message === "string") return data.message;
  if (typeof data.detail === "string") return data.detail;

  const messages = Object.entries(data).flatMap(([field, errors]) => {
    const list = Array.isArray(errors) ? errors : [errors];
    return list.map((err) => `${field.replace(/_/g, " ")}: ${err}`);
  });

  return messages.length ? messages.join(" | ") : fallback;
};

const ProfileAvatar = ({ src, name }) =>
  src ? (
    <img
      src={src}
      alt={name}
      className="profile-avatar"
      style={{ objectFit: "cover" }}
    />
  ) : (
    <div className="profile-avatar">
      {(() => {
        const parts = (name || "-").trim().split(/\s+/);
        const first = parts[0].charAt(0);
        const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : "";
        return `${first}${last}`.toUpperCase();
      })()}
    </div>
  );

const CreditAdjustments = () => {
  const { canEdit } = usePageAccess("credits-billing");
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const [type, setType] = useState("add");
  const [companyData, setCompanyData] = useState(null);
  const [moduleOptions, setModuleOptions] = useState([]);

  const companyId = location.state?.companyId;

  const fetchCompanyCredits = async () => {
    if (!companyId) return;

    try {
      const res = await getCompanyCreditDetails(companyId);

      if (Math.floor(res?.status / 100) === 2) {
        setCompanyData(res?.data?.data || null);
      }
    } catch (err) {
      console.error("Failed to load company credit details:", err);
    }
  };

  useEffect(() => {
    fetchCompanyCredits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;

    const fetchModules = async () => {
      try {
        const res = await getCompanyModulesLookup(companyId);
        const list = Array.isArray(res?.data)
          ? res.data
          : res?.data?.data || res?.data?.results || [];

        if (Math.floor(res?.status / 100) === 2 && Array.isArray(list)) {
          setModuleOptions(
            list.map((module) => ({ label: module.name, value: module.id })),
          );
        }
      } catch (err) {
        console.error("Failed to load modules lookup:", err);
      }
    };

    fetchModules();
  }, [companyId]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleCancel = () => {
    form.resetFields();
  };

  const handleConfirm = async () => {
    if (!companyId) {
      message.error("No company selected.");
      return;
    }

    let values;
    try {
      values = await form.validateFields();
    } catch (error) {
      return;
    }

    try {
      const res = await submitCreditAdjustment(companyId, {
        type,
        reason: values.reason,
        amount: Number(values.amount),
        module_id: values.category,
      });

      if (Math.floor(res?.status / 100) === 2) {
        message.success(res?.data?.message || "Adjustment applied successfully");
        form.resetFields();
        fetchCompanyCredits();
      } else {
        message.error(
          getErrorMessage(res?.data, "Failed to apply adjustment."),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to apply adjustment."),
      );
    }
  };

  return (
    <div className="credit-adjustments">
      <div className="back-btn">
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          Back
        </Button>
      </div>
      <div className="header-wrapper">
        <div className="title">{companyData?.legal_name || "-"}</div>
      </div>

      <div className="row">
        <div className="left-column">
          <div className="profile-card">
            <div className="profile-header">
              <ProfileAvatar
                src={resolveAvatarUrl(companyData?.avatar)}
                name={companyData?.legal_name}
              />
            </div>

            <div className="profile-body">
              <div className="company-name">
                {companyData?.legal_name || "-"}
              </div>
              <div className="company-id">ID: {companyData?.id ?? "-"}</div>

              <div
                style={{ display: "flex", gap: 8, justifyContent: "center" }}
              >
                <span
                  className={`badge ${companyData?.fueling_locked ? "badge-fuel" : "badge-active"}`}
                >
                  Fueling Status:{" "}
                  {companyData?.fueling_locked ? "Inactive" : "Active"}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="right-column">
          <div className="tabs">
            <GenericStatCard
              title="Total Credit Balance"
              value={formatCurrency(companyData?.total_balance)}
              icon={creditIcon}
            />
          </div>
          <div className="tabs">
            <GenericStatCard
              title="Allocated"
              value={formatCurrency(companyData?.allocated_total)}
              icon={dataUsageIcon}
            />
          </div>
          <div className="tabs">
            <GenericStatCard
              title="Available"
              value={formatCurrency(companyData?.available_balance)}
              icon={paymentIcon}
            />
          </div>
          <div className="tabs">
            <GenericStatCard
              title="Pending Top-Ups"
              value={formatCurrency(companyData?.pending_topups_total)}
              icon={laptopCarIcon}
            />
          </div>
        </div>
      </div>

      {canEdit && (
        <div className="adjustment-card">
          <div className="card-header">
            <pdiv className="title">Adjustment Type</pdiv>
            <div className="sub-title">
              Create a new account with specific role access
            </div>
          </div>

          {/* Toggle */}
          <div className="adjustment-toggle">
            <div
              className={`toggle-btn ${type === "add" ? "active" : ""}`}
              onClick={() => setType("add")}
            >
              Add Credit
            </div>
            <div
              className={`toggle-btn ${type === "deduct" ? "active" : ""}`}
              onClick={() => setType("deduct")}
            >
              Deduct Credit
            </div>
          </div>

          {/* Form */}
          <Form form={form} layout="vertical">
            {/* Row */}
            <div className="form-row">
              {/* Amount */}
              <div className="form-group">
                <Form.Item
                  label={<>Adjustment Amount</>}
                  name="amount"
                  rules={[
                    { required: true, message: "Please enter amount" },
                    {
                      pattern: /^[0-9]+(\.[0-9]{1,2})?$/,
                      message: "Enter a valid number",
                    },
                  ]}
                >
                  <Input
                    prefix="$"
                    placeholder={`${type === "deduct" ? "-" : ""} 0.0`}
                    onKeyPress={(e) => {
                      // allow only numbers and dot
                      if (!/[0-9.]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                </Form.Item>
              </div>

              {/* Category */}
              <div className="form-group">
                <Form.Item
                  label={<>Category</>}
                  name="category"
                  rules={[{ required: true, message: "Please select category" }]}
                >
                  <Select placeholder="Select" options={moduleOptions} />
                </Form.Item>
              </div>
            </div>

            {/* Reason */}
            <div className="form-group">
              <Form.Item label="Add Reason" name="reason">
                <TextArea rows={4} placeholder="Enter a reason..." />
              </Form.Item>
            </div>
          </Form>

          {/* Footer Buttons */}
          <div className="card-footer">
            <Button className="cancel-btn" onClick={handleCancel}>
              Cancel
            </Button>

            <Button
              type="primary"
              className="confirm-btn"
              onClick={handleConfirm}
            >
              Confirm Adjustment
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditAdjustments;
